import { MAX_RANDOM_BRANCHES, randomPortId } from "@velizhanin-dev/bot-scenario"
import { MessageCircleQuestion, Plus, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import type { DraftNode } from "@/entities/bot-builder"
import { makeButtonId } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { Textarea } from "@/shared/ui/textarea"
import {
	botPath,
	type LoadedDraft,
	useBotWorkspace,
	WithDraft,
	WorkspaceSection,
} from "@/widgets/bot-workspace"
import { AnimatePresence, motion } from "framer-motion"
import { listItem } from "@/shared/lib/motion"
import { LoadSwap } from "@/shared/ui/load-swap"

const SUBTITLE =
	"Когда человек пишет то, чего бот не ждёт (не кнопку и не ключевое слово), бот отвечает одним из этих сообщений, каждый раз случайным."
const NEW_ANSWER = "Новый ответ"

type Branch = { id: string; label: string; weight: number }

/** «Конструктор → Случайные ответы»: поток «Не понял» как список ответов, один из которых выбирается случайно. */
export function RandomAnswersPage() {
	return (
		<WithDraft title="Если бот не понял" subtitle={SUBTITLE}>
			{(state) => <RandomAnswers state={state} />}
		</WithDraft>
	)
}

function AnswerEditor({
	node,
	index,
	onSave,
	onDelete,
}: {
	node: DraftNode
	index: number
	onSave: (text: string) => void
	onDelete: () => void
}) {
	const saved = String(node.config.text ?? "")
	const [text, setText] = useState(saved)
	useEffect(() => setText(saved), [saved])
	return (
		<div className="space-y-2 rounded-2xl border border-border bg-card p-3">
			<div className="flex items-center justify-between gap-2">
				<span className="text-sm font-medium">Ответ {index + 1}</span>
				<Button
					variant="ghost"
					size="icon"
					aria-label={`Удалить ответ ${index + 1}`}
					onClick={onDelete}
				>
					<Trash2 />
				</Button>
			</div>
			<Textarea
				rows={3}
				placeholder="Один из вариантов: бот выберет случайный"
				value={text}
				onChange={(event) => setText(event.target.value)}
			/>
			<div className="flex justify-end">
				<Button
					size="sm"
					variant="outline"
					disabled={text === saved || !text.trim()}
					onClick={() => onSave(text)}
				>
					Сохранить
				</Button>
			</div>
		</div>
	)
}

function RandomAnswers({ state }: { state: LoadedDraft }) {
	const { bot } = useBotWorkspace()
	const { confirm } = useConfirm()
	const { draft, actions } = state
	const fallback = draft.flows.find((flow) => flow.kind === "FALLBACK")
	const nodeById = new Map(draft.nodes.map((node) => [node.id, node]))
	const edgeTarget = (nodeId: string, port: string) =>
		draft.edges.find((edge) => edge.sourceNodeId === nodeId && edge.sourcePort === port)
			?.targetNodeId

	const start = draft.nodes.find((node) => node.flowId === fallback?.id && node.type === "start")
	const first = start ? nodeById.get(edgeTarget(start.id, "next") ?? "") : undefined
	const random = first?.type === "random" ? first : undefined
	const branches = (random?.config.branches as Branch[] | undefined) ?? []
	const answers: { node: DraftNode; branch?: Branch }[] = random
		? branches.flatMap((branch) => {
				const node = nodeById.get(edgeTarget(random.id, randomPortId(branch.id)) ?? "")
				return node?.type === "message" ? [{ node, branch }] : []
			})
		: first?.type === "message"
			? [{ node: first }]
			: []
	const custom = !!first && first.type !== "message" && first.type !== "random"

	if (!fallback || !start) {
		return (
			<WorkspaceSection title="Если бот не понял" subtitle={SUBTITLE}>
				<EmptyState title="В сценарии нет воронки «Не понял»" />
			</WorkspaceSection>
		)
	}

	const add = async () => {
		if (!first) {
			await actions.createNode({
				flowId: fallback.id,
				type: "message",
				config: { text: NEW_ANSWER },
				connectFrom: { nodeId: start.id, port: "next" },
			})
			return
		}
		if (!random) {
			const [a, b] = [makeButtonId(), makeButtonId()]
			const created = await actions.createNode({
				flowId: fallback.id,
				type: "random",
				config: {
					branches: [
						{ id: a, label: "Ответ 1", weight: 1 },
						{ id: b, label: "Ответ 2", weight: 1 },
					],
				},
				position: { x: start.positionX + 320, y: start.positionY },
			})
			if (!created) return
			await actions.putEdge({
				sourceNodeId: start.id,
				sourcePort: "next",
				targetNodeId: created.node.id,
			})
			await actions.putEdge({
				sourceNodeId: created.node.id,
				sourcePort: randomPortId(a),
				targetNodeId: first.id,
			})
			await actions.createNode({
				flowId: fallback.id,
				type: "message",
				config: { text: NEW_ANSWER },
				connectFrom: { nodeId: created.node.id, port: randomPortId(b) },
			})
			return
		}
		if (branches.length >= MAX_RANDOM_BRANCHES) {
			toast.error(`Не больше ${MAX_RANDOM_BRANCHES} случайных ответов`)
			return
		}
		const id = makeButtonId()
		const updated = await actions.updateNode(random.id, {
			config: {
				...random.config,
				branches: [...branches, { id, label: `Ответ ${branches.length + 1}`, weight: 1 }],
			},
		})
		if (!updated) return
		await actions.createNode({
			flowId: fallback.id,
			type: "message",
			config: { text: NEW_ANSWER },
			connectFrom: { nodeId: random.id, port: randomPortId(id) },
		})
	}

	const remove = async (answer: { node: DraftNode; branch?: Branch }) => {
		if (random && answer.branch) {
			const rest = branches.filter((branch) => branch.id !== answer.branch?.id)
			if (rest.length >= 2) {
				await actions.updateNode(random.id, { config: { ...random.config, branches: rest } })
			} else {
				const remaining = rest[0] ? edgeTarget(random.id, randomPortId(rest[0].id)) : undefined
				if (remaining)
					await actions.putEdge({
						sourceNodeId: start.id,
						sourcePort: "next",
						targetNodeId: remaining,
					})
				await actions.deleteNode(random.id)
			}
		}
		await actions.deleteNode(answer.node.id)
	}

	return (
		<WorkspaceSection
			title="Если бот не понял"
			subtitle={SUBTITLE}
			actions={
				!custom ? (
					<Button className="rounded-xl" onClick={() => void add()}>
						<Plus />
						Добавить случайный ответ
					</Button>
				) : null
			}
		>
			<LoadSwap loading={false} id={custom ? "custom" : answers.length === 0 ? "empty" : "list"}>
				{custom ? (
					<EmptyState icon={MessageCircleQuestion} title="Воронка «Не понял» настроена вручную">
						Он устроен сложнее списка ответов:{" "}
						<Link
							className="text-primary"
							to={botPath(bot.id, `constructor/funnels/${fallback.id}`)}
						>
							откройте его в редакторе воронки
						</Link>
						.
					</EmptyState>
				) : answers.length === 0 ? (
					<EmptyState icon={MessageCircleQuestion} title="Случайных ответов нет">
						Бот промолчит в ответ на непонятное сообщение.
					</EmptyState>
				) : (
					<div>
						{/* Ответ добавляется и удаляется по высоте, остальные доезжают плавно. */}
						<AnimatePresence initial={false}>
							{answers.map((answer, index) => (
								<motion.div key={answer.node.id} {...listItem}>
									<div className="pb-3">
										<AnswerEditor
											node={answer.node}
											index={index}
											onSave={(text) =>
												void actions.updateNode(answer.node.id, {
													config: { ...answer.node.config, text },
												})
											}
											onDelete={() =>
												confirm({
													title: `Удалить ответ ${index + 1}?`,
													message: "Бот перестанет отправлять этот вариант.",
													confirmText: "Удалить",
													onConfirm: () => remove(answer),
												})
											}
										/>
									</div>
								</motion.div>
							))}
						</AnimatePresence>
					</div>
				)}
			</LoadSwap>
		</WorkspaceSection>
	)
}
